/* eslint-disable no-console */
import i18next from 'i18next';
import {
  ICheckItem,
  IDictionaryItem,
  IForm,
  IFavoriteProps,
  IModules,
  IModuleTile,
  IOnboardingModalProps,
  IProgramGroup,
  IUserSettings,
} from '../interfaces/interfaces';
import { availableModules, getModuleComponents } from '../modules';
import randomId from './randomId';

declare global {
  interface Window {
    KfwJsConfig: {
      partnerportalCockpit: {
        API_URL_REST: string;
        API_URL_GRAPHQL: string;
      };
    };
  }
}

const API_URL_REST = window.KfwJsConfig ? window.KfwJsConfig.partnerportalCockpit.API_URL_REST : '';
const API_URL_GRAPHQL = window.KfwJsConfig ? window.KfwJsConfig.partnerportalCockpit.API_URL_GRAPHQL : '';

const propsQuery = `props{
  ...on LinkedIconModuleProps {
    headline,
    ariaLabel,
    linkText,
    linkHref,
    iconUrl,
    dataAttributes {
      key,
      value
    },
    locked,
  }
  ...on RegistrationModuleProps {
    headline,
    textContent
  }
  ...on SearchModuleProps {
    headline,
    textContent,
    containerClassName,
    searchActionUrl,
    inputPlaceHolder,
    searchParams{
        key, value
    }
  }
  ...on InfocenterModuleProps {
    headline,
    phone,
    phoneHref,
    phoneAriaLabel,
    noticeCost,
    serviceTime,
    linkText,
    linkHref,
    linkAriaLabel,
    textContent,
    dataAttributes {
      key,
      value
    },
  }
  ...on FavoritesModuleProps {
    headline,
    favorites {
      linkText,
      linkHref,
      id,
      sortIndex,
    }
  }
  ...on TextModuleProps {
    headline,
    headlineContentLevel,
    headlineContent,
    textContent,
    linkText,
    linkHref,
    dataAttributes {
      key,
      value
    }
  }
  ...on ProductModuleProps {
    headline,
    creditId,
    creditType,
    textContent,
    linkHref,
    linkText,
    ariaLabel,
  }
  ... on ContactModuleProps {
    headline,
    privacyLinkText,
    privacyLinkHref
  }
  ... on AdvantageCommunicationModuleProps {
    headline,
    personalisationText,
    advantageList
  }
  ... on PersonalizationModuleProps {
    headline,
    linkText,
    iconUrl
  }
  ... on AppStoreModuleProps {
    headline,
    iconUrl,
    linkText,
  }
  ... on FormsModuleProps {
    headline,
    searchUrl
  }
}`;

const getGQLQueryString = (tileIds: string[]): string => {
  return `{
    tiles(tileRefIds: ${JSON.stringify(tileIds)}) {
      tileRefId,
      module,
      ${propsQuery}
    }
    dictionary {
      dictionaryItems {
        key,
        value
      }
    }
  }`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const loadUserConfig = (fromLocal?: boolean): Promise<{ tileList: IModuleTile[]; config: any }> => {
  return new Promise((resolve, reject) => {
    fetch(API_URL_REST, {
      method: 'GET',
      credentials: fromLocal ? 'omit' : 'include',
      headers: {
        Accept: 'application/json',
      },
    })
      .then((r) => {
        return r.json();
      })
      .then((r) => {
        let obj;
        try {
          obj = JSON.parse(r.configuration);
          if (!obj) {
            console.error('Received configuration not parsable!', obj);
            reject(new Error('Received configuration not parsable!'));
          }
        } catch {
          console.error('Received configuration not parsable!');
          obj = { tileList: [] };
        }
        resolve({ tileList: obj.tileList, config: obj });
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const loadGQLData = (
  query: string,
  fromLocal?: boolean,
): Promise<{ tiles: IModuleTile[]; dictionary: { dictionaryItems: IDictionaryItem[] } }> => {
  return new Promise((resolve, reject) => {
    fetch(API_URL_GRAPHQL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: query,
      credentials: fromLocal ? 'omit' : 'include',
    })
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        if (data.errors) {
          console.error('GraphQL-Error', data.errors);

          reject(data.errors);
          return;
        }
        resolve(data.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const camelize = (text: string, separator: string): string => {
  // Assume separator is _ if no one has been provided.
  const sep = separator || '_';

  // Cut the string into words
  const words = text.split(sep);

  // Concatenate all capitalized words to get camelized string
  let result = '';
  let i;
  for (i = 0; i < words.length; i += 1) {
    const word = words[i];
    const capitalizedWord = word.charAt(0).toUpperCase() + word.slice(1);
    result += capitalizedWord;
  }

  return result;
};

const mergeTilesWithConfig = (fetchedUserConfig: IModuleTile[], tilesData: IModuleTile[]): IModuleTile[] => {
  return fetchedUserConfig
    .map((ct) => {
      if (!ct.tileRefId) {
        console.warn('There is no tileRefId for tile');
        return null;
      }

      // find ref tile
      const [refTile] = tilesData.filter((rt) => {
        return rt.tileRefId?.toString() === (ct.tileRefId || '').toString();
      });

      if (!refTile) {
        // refTile not found
        // eslint-disable-next-line no-console
        console.warn('There is no tileRef for id:', ct.tileRefId);
        return null;
      }

      // merge props
      const mergedTile = { ...refTile, ...ct };
      mergedTile.props = { ...refTile.props, ...ct.props };
      mergedTile.id = randomId('ut-');
      mergedTile.module = camelize(mergedTile.module, '_');
      let defaultLayout = {
        x: 0,
        y: 0,
        w: 1,
        h: 1,
        minW: 1,
        minH: 1,
        maxW: 1,
        maxH: 1,
        isResizable: false,
        isStatic: false,
      };

      const avM = availableModules as IModules;
      if (avM[mergedTile.module].defaultLayout) {
        defaultLayout = { ...defaultLayout, ...avM[mergedTile.module].defaultLayout };
      }

      // transform:
      const userLayout = { x: ct.layout?.x || 0, y: ct.layout?.y || 0, i: ct.id || mergedTile.id };
      const refTileLayout = {
        w: refTile.layout?.w || defaultLayout.w,
        h: refTile.layout?.h || defaultLayout.h,
        minW: refTile.layout?.minW || defaultLayout.minW,
        minH: refTile.layout?.minH || defaultLayout.minH,
        maxW: refTile.layout?.maxW || defaultLayout.maxW,
        maxH: refTile.layout?.maxH || defaultLayout.maxH,
        isResizable: false,
        static: refTile.layout?.static || defaultLayout.isStatic,
      };

      mergedTile.layout = { ...defaultLayout, ...refTileLayout, ...userLayout };
      return mergedTile;
    })
    .filter((ct: IModuleTile | null) => ct !== null) as IModuleTile[];
};

const prepareForSave = (tileList: IModuleTile[]) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataToPersist: any[] = [];

  const moduleComponents = getModuleComponents(availableModules);

  [...tileList].forEach((d) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dCopy = { ...d } as any;
    dCopy.props = {};

    if (moduleComponents[d.module].persistProps) {
      moduleComponents[d.module].persistProps?.forEach((propName) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dCopy.props[propName] = (d.props as any)[propName];
      });
    }

    delete dCopy.layout.minW;
    delete dCopy.layout.minH;
    delete dCopy.layout.maxW;
    delete dCopy.layout.maxH;
    delete dCopy.layout.isResizable;
    delete dCopy.layout.isStatic;
    delete dCopy.layout.static;
    delete dCopy.preview;
    delete dCopy.isSelected;
    delete dCopy.module;

    dataToPersist.push(dCopy);
  });
  return dataToPersist;
};

const saveConfiguration = (tileList: IModuleTile[], settings: IUserSettings): Promise<IModuleTile[]> => {
  const cleanTileList = prepareForSave(tileList);
  return new Promise((resolve, reject) => {
    const requestBody = { configuration: JSON.stringify({ tileList: cleanTileList, settings }) };
    fetch(API_URL_REST, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    })
      .then(() => {
        resolve([]);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const loadTopics = (fromLocal?: boolean): Promise<ICheckItem[]> => {
  return new Promise((resolve, reject) => {
    fetch(API_URL_GRAPHQL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: `query{
        topics {
          title,
          id,
          topics {
              title,
              id,
              tileRefId,
              tiles{
                tileRefId
                module,
                ${propsQuery},
                preview {
                    headline,
                    description,
                    iconUrl
                }
            }
          }
      }}`,
      credentials: fromLocal ? 'omit' : 'include',
    })
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        if (data.errors) {
          console.error('GraphQL-Error', data.errors);

          reject(data.errors);
          return;
        }
        resolve(data.data.topics);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const loadStoreData = (fromLocal?: boolean): Promise<ICheckItem[]> => {
  return new Promise((resolve, reject) => {
    fetch(API_URL_GRAPHQL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: `query {
        categories {
            id,
            title,
            tileRefIds,
            tiles {
                tileRefId,
                module,
                ${propsQuery},
                preview {
                    headline,
                    description,
                    iconUrl
                },
                productReference {
                  displayNumber,
                  creditType
                },
            }
        }
     }`,
      credentials: fromLocal ? 'omit' : 'include',
    })
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        if (data.errors) {
          console.error('GraphQL-Error', data.errors);

          reject(data.errors);
          return;
        }
        resolve(data.data.categories);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const loadProducts = (fromLocal?: boolean): Promise<{ products: ICheckItem[]; productGroups: IProgramGroup[] }> => {
  return new Promise((resolve, reject) => {
    fetch(API_URL_GRAPHQL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: `query{
        products {
            id,
            productGroupId,
            title,
            tileRefId,
            displayNumber,
            tiles {
                tileRefId,
                module,
                ${propsQuery},
                preview {
                    headline,
                    description,
                    iconUrl
                }
            }
        },
        productGroups{
          id,
          title,
          productIds
        }
    }`,
      credentials: fromLocal ? 'omit' : 'include',
    })
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        if (data.errors) {
          console.error('GraphQL-Error', data.errors);

          reject(data.errors);
          return;
        }
        resolve({ products: data.data.products, productGroups: data.data.productGroups });
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const loadOnboarding = (): Promise<IOnboardingModalProps> => {
  return new Promise((resolve, reject) => {
    fetch(API_URL_GRAPHQL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: `query {
        onboarding {
          header,
          title,
          overlayItems {
            id,
            headline,
            textContent,
            iconUrl,
            linkText,
            dataAttributes {
              key,
              value
            }
          }
        }
      }
      `,
    })
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        if (data.errors) {
          console.error('GraphQL-Error', data.errors);

          reject(data.errors);
          return;
        }
        resolve(data.data.onboarding);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const loadFormTileData = (searchUrl: string, fromLocal?: boolean): Promise<IForm[]> => {
  return new Promise((resolve, reject) => {
    fetch(searchUrl, {
      method: 'GET',
      // headers: {
      //   Accept: 'application/json',
      // },
      credentials: fromLocal ? 'omit' : 'include',
    })
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        if (data.errors) {
          console.error('Haupia-Error', data.errors);

          reject(data.errors);
          return;
        }
        resolve(data.results);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const saveFavorites = (favoriteList : IFavoriteProps[], fromLocal?: boolean): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    let formattedFavorites = '';
    favoriteList.forEach((item) => {
      formattedFavorites += `{ id: ${item.id}, sortIndex: ${item.sortIndex} }`;   
    });
    fetch(API_URL_GRAPHQL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      credentials: fromLocal ? 'omit' : 'include',
      body: `mutation {
        saveFavorites(favorites: [${formattedFavorites}])
      }
      `,
    })
      .then((r) => {
        return r.json();
      })
      .then((data) => {
        if (data.errors) {
          console.error('GraphQL-Error', data.errors);

          reject(data.errors);
          return;
        }
        resolve(data.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

const initI18n = (dictionaryItems: IDictionaryItem[]): void => {
  const translations = dictionaryItems.reduce((obj, item) => ({ ...obj, [`${item.key}`]: item.value }), {});
  i18next.addResourceBundle('de', 'translations', translations, true, true);
};

export {
  loadGQLData,
  loadUserConfig,
  camelize,
  mergeTilesWithConfig,
  getGQLQueryString,
  saveConfiguration,
  loadTopics,
  loadProducts,
  loadOnboarding,
  loadStoreData,
  loadFormTileData,
  initI18n,
  saveFavorites,
};
